import React from 'react';
import { Star } from 'lucide-react';
import { motion } from 'framer-motion';

const ListProviders = ({ providerList, setSelectedProvider }) => {
  const renderRating = (rating) => {
    return (
      <div className="flex items-center gap-0.5 sm:gap-1">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`w-3 h-3 sm:w-3.5 sm:h-3.5 transition-colors duration-200 ${
              i < Math.floor(rating) ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
            }`}
          />
        ))}
        <span className="ml-0.5 sm:ml-1 text-[10px] sm:text-xs text-gray-400">({rating})</span>
      </div>
    );
  };

  return (
    <div className="p-2 sm:p-4">
      <ul className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-6">
        {providerList.map((provider) => (
          <li 
            key={provider.id} 
            className="group relative h-[260px] sm:h-[420px]"
          >
            <div 
              className="relative bg-white/5 backdrop-blur-sm rounded-lg sm:rounded-2xl overflow-hidden
                         hover:bg-white/10 transition-all duration-300 cursor-pointer
                         border border-white/10 hover:border-white/20 h-full
                         flex flex-col"
            >
              {/* Image Container - Optimized for mobile */}
              <div className="relative h-28 sm:h-48 flex-shrink-0">
                <img 
                  src={provider.image_url} 
                  alt={provider.name} 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              </div>

              {/* Content - Compact layout for mobile */}
              <div className="flex-grow flex flex-col p-2 sm:p-5">
                {/* Title, Price, and Rating section */}
                <div className="mb-1.5 sm:mb-3">
                  <div className="flex justify-between items-start gap-1 sm:gap-2 mb-1 sm:mb-1.5">
                    <h3 className="font-semibold text-white text-sm sm:text-lg leading-tight line-clamp-2 flex-1">
                      {truncateText(provider.name, 3)}
                    </h3>
                    <span className="px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-white/10 text-yellow-400 
                                   text-[10px] sm:text-sm font-medium whitespace-nowrap">
                      £{provider.price}
                    </span>
                  </div>
                  {renderRating(provider.overallRating)}
                </div>

                {/* Description - Smaller text and fewer lines for mobile */}
                <div className="flex-grow">
                  <p className="text-gray-400 text-[11px] sm:text-sm leading-relaxed line-clamp-2 sm:line-clamp-3">
                    {provider.description}
                  </p>
                </div>

                {/* Action Button - Compact for mobile */}
                <div className="pt-1.5 sm:pt-4 mt-auto">
                  <button 
                    onClick={() => setSelectedProvider(provider)}
                    className="w-full px-2 sm:px-4 py-1.5 sm:py-2.5 rounded-md sm:rounded-xl 
                             font-medium text-[11px] sm:text-sm
                             bg-gradient-to-r from-yellow-400 to-green-400
                             text-gray-900 hover:shadow-lg hover:from-yellow-300 
                             hover:to-green-300 transition-all duration-300
                             focus:ring-2 focus:ring-yellow-400/50 focus:outline-none
                             active:scale-95 sm:active:scale-100"
                  >
                    View Details
                  </button>
                </div>
              </div>

              {/* Hover Overlay */}
              <div className="absolute inset-0 border-2 border-transparent
                            hover:border-yellow-400/50 rounded-lg sm:rounded-2xl
                            transition-all duration-300 pointer-events-none" />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function truncateText(text, limit) {
  const words = text.split(' ');
  if (words.length > limit) {
    return `${words.slice(0, limit).join(' ')}...`;
  }
  return text;
}

export default ListProviders;
